import ExpirationDateField from "./ExpirationDateField";
import { expirationDatePayload } from "../../utils/expirationDate";
import { currentReceivingPermissions, useReceivingPermissions } from "../../utils/receivingPermissions";
import { useAuthorizedReceivingDestinations, useDestinationSaveQueue, ReceivingLocationPicker, permittedReceivingLocations, receivingLineLocation } from "./ReceivingDestinations";
import { itemMatchesSupplier } from "../../utils/supplierIdentity";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../../api/axiosInstance";
import { resolveRequestBusinessUnitId } from "../../../currency/currencyStorage";
import useIsMobile from "../../../hooks/useIsMobile";
import AddItemPopup from "./AddItemPopup";
import { receivingPopupStatus, useReceivingPopupUi, receivingErrorMessage } from "./receivingPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

const numberInputStyle = `
  .purchasing-receiving-ui input[type=number]::-webkit-inner-spin-button,
  .purchasing-receiving-ui input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .purchasing-receiving-ui input[type=number] {
    -moz-appearance: textfield;
  }
`;

const popupCss = `
  .erp-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
  .erp-modal{width:min(1480px,97vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-text);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .erp-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px 14px;border-bottom:1px solid var(--pu-border)}
  .erp-headMain{flex:1;min-width:0}
  .erp-headActions{display:flex;align-items:center;gap:10px;flex-shrink:0}
  .erp-title{margin:0;font-size:clamp(1.25rem,2vw,1.72rem);font-weight:900;line-height:1.05}
  .erp-sub{margin-top:8px;font-size:.9rem;color:var(--pu-muted)}
  .erp-badge{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.72rem;font-weight:800;letter-spacing:.08em}
  .erp-badge--done{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .erp-closeBtn{width:40px;min-width:40px;height:40px;border-radius:14px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.92rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .erp-closeBtn:hover{transform:translateY(-1px);background:var(--pu-soft);border-color:var(--pu-border);box-shadow:none}
  .erp-body{padding:16px 24px 24px}
  .erp-section{border:1px solid var(--pu-border);border-radius:20px;padding:18px;background:var(--pu-soft);box-shadow:none}
  .erp-section + .erp-section{margin-top:16px}
  .erp-sectionTitle{margin:0 0 4px;font-size:1rem;font-weight:850}
  .erp-sectionSub{margin:0 0 14px;color:var(--pu-muted);font-size:.82rem}
  .erp-row{display:grid;gap:16px}
  .erp-row--header{grid-template-columns:minmax(150px,.7fr) minmax(200px,1fr) minmax(210px,.95fr) minmax(150px,.72fr) minmax(190px,.86fr) minmax(170px,.8fr)}
  .erp-field input,.erp-field select{width:100%}
  .erp-modal .form-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
  .erp-modal .form-control,.erp-modal .form-select{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-surface)!important;color:var(--pu-text)!important;padding:10px 12px;min-height:42px}
  .erp-modal .form-control::placeholder{color:var(--pu-muted)}
  .erp-modal .form-select option{background:var(--pu-surface);color:var(--pu-text)}
  .erp-readonly{background:var(--pu-soft)!important;border-color:var(--pu-border)!important}
  .erp-btn{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);padding:9px 13px;font-size:.86rem;font-weight:750;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .erp-btn:hover{transform:translateY(-1px);background:var(--pu-soft);box-shadow:none}
  .erp-btn:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}
  .erp-btn--primary{border-color:var(--pu-primary);background:var(--pu-primary);color:#ffffff}
  .erp-btn--danger{border-color:var(--pu-danger);color:var(--pu-danger);background:var(--pu-danger-bg)}
  .erp-btn--cyan{border-color:var(--pu-border);color:var(--pu-text);background:var(--pu-soft)}
  .erp-btn--soft{border-color:var(--pu-border);background:var(--pu-soft)}
  .erp-pickerTrigger{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;text-align:left}
  .erp-pickerTrigger span:first-child{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .erp-pickerTrigger span:last-child{flex-shrink:0;font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;color:var(--pu-text)}
  .erp-linesTop,.erp-manualHead{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .erp-linesTop{margin-bottom:12px}
  .erp-linesTop h6,.erp-manualHead h6{margin:0;font-size:1rem;font-weight:850}
  .erp-linesTop p,.erp-manualHead p{margin:6px 0 0;color:var(--pu-text);font-size:.8rem}
  .erp-inlineActions,.erp-actions,.erp-manualActions,.erp-pickerActions,.erp-lineActions,.erp-candidateActions{display:flex;gap:10px;flex-wrap:wrap}
  .erp-inlineActions,.erp-actions,.erp-manualActions,.erp-pickerActions,.erp-lineActions,.erp-candidateActions{justify-content:flex-end}
  .erp-manualCard{border:1px solid var(--pu-border);border-radius:20px;padding:16px;background:var(--pu-soft);margin-bottom:14px}
  .erp-manualGrid{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(120px,.45fr) minmax(240px,.8fr);gap:14px}
  .erp-infoCard,.erp-lineMeta,.erp-candidateCard{border-radius:16px;background:var(--pu-soft);border:1px solid var(--pu-border)}
  .erp-infoCard,.erp-lineMeta{padding:12px}
  .erp-infoLabel,.erp-lineMetaLabel,.erp-candidateMetaLabel{display:block;color:var(--pu-muted);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
  .erp-infoValue,.erp-lineMetaValue{display:block;margin-top:4px;font-size:.92rem;font-weight:800;color:var(--pu-muted)}
  .erp-tableShell{border:1px solid var(--pu-border);border-radius:22px;padding:14px 16px 16px;background:var(--pu-soft);box-shadow:none}
  .erp-tableWrap{overflow:hidden;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
  .erp-table{width:100%;border-collapse:separate;border-spacing:0;table-layout:fixed;background:transparent!important;border:none!important}
  .erp-table th,.erp-table td{padding:12px 14px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .erp-table th{position:sticky;top:0;z-index:2;background:var(--pu-primary);color:#ffffff;font-size:.73rem;text-transform:uppercase;letter-spacing:.06em;font-weight:800}
  .erp-table th:first-child{border-top-left-radius:14px}
  .erp-table th:last-child{border-top-right-radius:14px}
  .erp-table tbody tr{background:var(--pu-surface)}
  .erp-table tbody tr:nth-child(even){background:var(--pu-soft)}
  .erp-table tbody tr:last-child td{border-bottom:none}
  .erp-tableItem{display:flex;flex-direction:column;gap:5px;min-width:0}
  .erp-tableItemTop{display:flex;align-items:center;gap:8px;min-width:0}
  .erp-itemName{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:850}
  .erp-itemMeta,.erp-tableCell--muted{color:var(--pu-muted);font-size:.78rem;line-height:1.35}
  .erp-miniBadge{display:inline-flex;align-items:center;padding:4px 8px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.64rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase}
  .erp-qtyCell{text-align:center;font-weight:800}
  .erp-tableInput{width:100%!important;min-width:0;text-align:center}
  .erp-emptyState{border:1px dashed var(--pu-border);border-radius:20px;padding:22px 20px;background:var(--pu-soft)}
  .erp-emptyState strong{display:block;font-size:1rem}
  .erp-emptyState span{display:block;margin-top:6px;color:var(--pu-text);font-size:.86rem;line-height:1.45}
  .erp-mobileLines{display:grid;gap:12px}
  .erp-lineCard{border:1px solid var(--pu-border);border-radius:18px;padding:14px;background:var(--pu-soft)}
  .erp-lineCardTop{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
  .erp-lineCardTitle{font-size:.96rem;font-weight:850;color:var(--pu-text);line-height:1.2}
  .erp-lineCardSub{margin-top:4px;color:var(--pu-muted);font-size:.78rem}
  .erp-lineCardMeta{margin-top:10px;color:var(--pu-muted);font-size:.82rem}
  .erp-lineGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:12px}
  .erp-alert{margin-top:12px;border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);border-radius:12px;padding:10px 12px;font-size:.84rem}
  .erp-pickerWrap{position:fixed;inset:0;z-index:10040;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .erp-pickerCard{width:min(680px,94vw);max-height:min(80vh,760px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .erp-pickerCard--wide{width:min(820px,94vw)}
  .erp-pickerHead{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 18px 12px;border-bottom:1px solid var(--pu-border)}
  .erp-pickerHead h6{margin:0;font-size:1rem;font-weight:850}
  .erp-pickerHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem}
  .erp-pickerBody{padding:14px 18px 18px}
  .erp-pickerSearch{margin-bottom:12px}
  .erp-pickerList{display:grid;gap:10px}
  .erp-pickerItem{width:100%;text-align:left;border-radius:16px;border:1px solid var(--pu-border);background:var(--pu-soft);padding:12px 14px;color:var(--pu-text);transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .erp-pickerItem:hover{transform:translateY(-1px);border-color:var(--pu-border);background:var(--pu-soft);box-shadow:none}
  .erp-pickerItemTitle{display:block;font-size:.95rem;font-weight:800}
  .erp-pickerItemMeta{display:block;margin-top:4px;font-size:.78rem;color:var(--pu-muted)}
  .erp-pickerEmpty{border:1px dashed var(--pu-border);border-radius:16px;padding:16px;text-align:center;color:var(--pu-text);font-size:.84rem}
  .erp-confirmText{margin:0;color:var(--pu-text);font-size:.92rem;line-height:1.5}
  .erp-confirmText strong{color:var(--pu-text)}
  .erp-candidateCard{padding:14px}
  .erp-candidateTop{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}
  .erp-candidateTitle{font-size:.95rem;font-weight:850}
  .erp-candidateMeta{margin-top:10px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
  .erp-candidateMetaBox{border-radius:14px;padding:10px 12px;background:var(--pu-soft);border:1px solid var(--pu-border)}
  @media (max-width:1080px){.erp-row--header{grid-template-columns:repeat(2,minmax(0,1fr))}.erp-manualGrid{grid-template-columns:minmax(0,1fr) minmax(120px,.4fr)}.erp-manualGrid .erp-infoCard{grid-column:1 / -1}}
  @media (max-width:640px){.erp-wrap{padding:8px;align-items:flex-start}.erp-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.erp-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.erp-headMain{min-width:0}.erp-headActions{gap:8px;justify-content:flex-end;align-self:start}.erp-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.erp-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.erp-badge{padding:5px 8px;font-size:.64rem}.erp-closeBtn{width:auto;min-width:76px;height:36px;padding:0 12px;border-radius:12px}.erp-body{padding:12px 14px 14px}.erp-section{padding:14px}.erp-row--header,.erp-lineGrid,.erp-manualGrid,.erp-candidateMeta{grid-template-columns:minmax(0,1fr)}.erp-linesTop,.erp-manualHead,.erp-inlineActions,.erp-actions,.erp-manualActions,.erp-pickerActions,.erp-lineActions,.erp-candidateActions{flex-direction:column;align-items:stretch}.erp-inlineActions .erp-btn,.erp-actions .erp-btn,.erp-manualActions .erp-btn,.erp-pickerActions .erp-btn,.erp-lineActions .erp-btn,.erp-candidateActions .erp-btn{width:100%}.erp-pickerWrap{padding:10px;align-items:flex-start}.erp-pickerCard,.erp-pickerCard--wide{width:100%;max-height:calc(100vh - 20px);border-radius:20px}.erp-pickerHead{padding:14px 14px 10px}.erp-pickerBody{padding:12px 14px 14px}}
`;

const today = () => new Date().toISOString().slice(0, 10);

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "en", {
      sensitivity: "base",
    })
  );

const sortLocations = (locations = []) =>
  [...locations].sort((left, right) => {
    const mainDelta = Number(Boolean(right?.is_main)) - Number(Boolean(left?.is_main));
    if (mainDelta !== 0) return mainDelta;

    const sortDelta = Number(left?.sort_order || 0) - Number(right?.sort_order || 0);
    if (sortDelta !== 0) return sortDelta;

    return String(left?.name || "").localeCompare(String(right?.name || ""), "en", {
      sensitivity: "base",
    });
  });

const formatQty = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  if (Number.isInteger(numericValue)) return String(numericValue);
  return numericValue.toFixed(2).replace(/\.00$/, "");
};

// Keep the original quantity for saving; round only its idle display.
const quantityInputValue = (line) => line.__dirty || !line.quantity_input
  ? line.quantity_input ?? ""
  : formatQty(line.quantity_input);

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)} EUR` : "-";
};

const formatDateLabel = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);
  return parsedDate.toLocaleDateString("en-GB");
};

const resolveUnitName = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value.name || "";
  return String(value);
};

const resolvePackaging = (source) => {
  const unitLabel = resolveUnitName(source?.unit || source?.item?.unit);
  const subunitLabel = resolveUnitName(source?.subunit || source?.item?.subunit);
  const packSize = Number(source?.pack_size || source?.item?.pack_size || 0);

  if (packSize > 1 && subunitLabel) return `${formatQty(packSize)} x ${subunitLabel}`;
  if (subunitLabel && unitLabel && subunitLabel !== unitLabel) return `${unitLabel} / ${subunitLabel}`;
  return subunitLabel || unitLabel || "-";
};

const getLineKey = (line) =>
  String(line?.id ?? line?.receipt_id ?? line?.po_line_id ?? `${line?.manual_item_id || "line"}-${Math.random()}`);

const getOrderLabel = (line) =>
  line?.order_code ||
  line?.order?.order_code ||
  line?.po_line?.order?.order_code ||
  (line?.po_line_id ? `PO #${line.po_line_id}` : "Manual line");

const getLineItemId = (line) => line?.manual_item_id || line?.item_id || line?.item?.id;

const getLinePrice = (line) =>
  line?.unit_price_received ?? null;

const normalizeLine = (line) => ({
  ...line,
  __key: getLineKey(line),
  __dirty: false,
  __saveError: false,
  __receivingPersisted: true,
  quantity_input:
    line?.quantity_received !== undefined &&
    line?.quantity_received !== null &&
    String(line.quantity_received) !== "0"
      ? String(line.quantity_received)
      : "",
});

export default function EditReceivingPopup({ receivingId, isOpen, onClose }) {
  const { t, themeId } = useReceivingPopupUi();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tableWrapRef = useRef(null);
  const loadRequestRef = useRef(0);

  const [receiving, setReceiving] = useState(null);
  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  const [error, setError] = useState("");
  const [locationSelectionRequest, setLocationSelectionRequest] = useState(0);
  const [showManualPanel, setShowManualPanel] = useState(false);
  const [manualItemId, setManualItemId] = useState("");
  const [manualQty, setManualQty] = useState("");
  const [showItemPopup, setShowItemPopup] = useState(false);
  const [manualItemPicker, setManualItemPicker] = useState({ open: false, query: "" });
  const [poLinePicker, setPOLinePicker] = useState({ open: false, query: "", loading: false });
  const [pendingPOLines, setPendingPOLines] = useState([]);
  const [confirmState, setConfirmState] = useState({ open: false, type: "", line: null });
  const formatUserLabel = (user) => {
    if (!user) return "-";
    return user.name || user.email || (user.id ? `User #${user.id}` : "-");
  };
  const formatDocumentInitiator = (record) => {
    const label = formatUserLabel(record?.initiated_by_user || record?.initiatedByUser);
    if (label !== "-") return label;
    const userId = record?.initiated_by_user_id || record?.initiatedByUserId;
    return userId ? `User #${userId}` : "-";
  };

  const access = useReceivingPermissions(receiving);
  const documentLocked = receiving?.status === "verified";
  const isLocked = documentLocked || !access.update;

  const deliveryNotesPath = useMemo(() => {
    const params = new URLSearchParams(searchParams);

    if (!params.get("business_unit_id")) {
      const activeBusinessUnitId = JSON.parse(localStorage.getItem("palantir_user") || "null")
        ?.active_business_unit_id;

      if (activeBusinessUnitId) {
        params.set("business_unit_id", String(activeBusinessUnitId));
      }
    }

    const query = params.toString();
    return `/purchasing/orders/delivery-notes${query ? `?${query}` : ""}`;
  }, [searchParams]);

  const visibleItems = useMemo(() => {
    if (!receiving?.supplier_id) return sortByName(items);
    return sortByName(
      items.filter((item) => itemMatchesSupplier(item, receiving.supplier_id))
    );
  }, [items, receiving?.supplier_id]);

  const selectedManualItem = useMemo(
    () => visibleItems.find((item) => String(item.id) === String(manualItemId)),
    [manualItemId, visibleItems]
  );

  const existingPOLineIds = useMemo(
    () => new Set(lines.filter((line) => line.po_line_id).map((line) => Number(line.po_line_id))),
    [lines]
  );

  const filteredManualItems = useMemo(() => {
    const query = manualItemPicker.query.trim().toLowerCase();
    if (!query) return visibleItems;
    return visibleItems.filter((item) => {
      const haystack = `${item.code || ""} ${item.name || ""} ${item.reference || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [manualItemPicker.query, visibleItems]);

  const filteredPendingPOLines = useMemo(() => {
    const query = poLinePicker.query.trim().toLowerCase();
    return pendingPOLines
      .filter((line) => !existingPOLineIds.has(Number(line.id)))
      .filter((line) => {
        if (!query) return true;
        const haystack = `${line.order?.order_code || ""} ${line.item?.name || ""} ${line.item?.code || ""}`.toLowerCase();
        return haystack.includes(query);
      });
  }, [existingPOLineIds, pendingPOLines, poLinePicker.query]);

  const closePopup = (result) => {
    onClose?.(result);
  };

  const dispatchRefresh = () => {
    window.dispatchEvent(new CustomEvent("refreshOrdersList"));
    window.dispatchEvent(new CustomEvent("refreshReceivingsList"));
    window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
  };

  const loadLines = async (targetReceivingId) => {
    const response = await axios.get(`/api/purchasing/receivings/${targetReceivingId}/lines`);
    setLines((response.data || []).map(normalizeLine));
  };

  const loadData = useCallback(async () => {
    if (!isOpen || !receivingId) return;
    const request = ++loadRequestRef.current;
    const businessUnitId = resolveRequestBusinessUnitId({});
    const isCurrent = () => request === loadRequestRef.current && businessUnitId === resolveRequestBusinessUnitId({});
    setReceiving(null); setLines([]); setItems([]); setInventoryLocations([]);
    try {
      setLoading(true);
      setLoadingLocations(true);
      setError("");

      const [headerRes, linesRes, contextRes] = await Promise.all([
        axios.get(`/api/purchasing/receivings/${receivingId}`),
        axios.get(`/api/purchasing/receivings/${receivingId}/lines`),
        axios.get("/api/purchasing/receivings/context"),
      ]);
      if (!isCurrent()) return;
      if (!Array.isArray(contextRes.data?.items) || !Array.isArray(contextRes.data?.locations)) throw new Error("Invalid receiving context");

      setReceiving({
        ...headerRes.data,
        received_date: headerRes.data?.received_date || today(),
      });
      setLines((linesRes.data || []).map(normalizeLine));
      setItems(sortByName(contextRes.data.items));
      setInventoryLocations(sortLocations(permittedReceivingLocations(contextRes.data.locations, { includeHistorical: headerRes.data?.status === "verified" })));
    } catch (loadError) {
      if (!isCurrent()) return;
      console.error("❌ [EditReceivingPopup] Error loading receiving data:", loadError);
      setReceiving(null); setLines([]); setItems([]); setInventoryLocations([]);
      setError(t(receivingErrorMessage(loadError, "Could not load this receiving.")));
    } finally {
      if (isCurrent()) { setLoading(false); setLoadingLocations(false); }
    }
  }, [isOpen, receivingId, t]);

  useEffect(() => {
    if (!isOpen || !receivingId) return;
    setShowManualPanel(false);
    setManualItemId("");
    setManualQty("");
    setShowItemPopup(false);
    setManualItemPicker({ open: false, query: "" });
    setPOLinePicker({ open: false, query: "", loading: false });
    setPendingPOLines([]);
    setConfirmState({ open: false, type: "", line: null });
    const requestCounter = loadRequestRef;
    loadData();
    return () => { requestCounter.current++; };
  }, [isOpen, receivingId, loadData]);

  const updateReceivingField = (field, value) => {
    setReceiving((previousReceiving) =>
      previousReceiving
        ? {
            ...previousReceiving,
            [field]: value,
          }
        : previousReceiving
    );
  };

  const saveHeader = async () => {
    if (!receiving?.id || !currentReceivingPermissions(receiving).update || documentLocked) return;

    await axios.patch(`/api/purchasing/receivings/${receiving.id}`, {
      delivery_note_number: receiving.delivery_note_number || "",
      received_date: receiving.received_date || today(),
      destination_location_id: receiving.destination_location_id || null,
    });
  };

  const handleLineInputChange = (lineKey, value) => {
    if (isLocked) return;
    setLines((previousLines) =>
      previousLines.map((line) =>
        line.__key === lineKey ? { ...line, quantity_input: value, __dirty: true } : line
      )
    );
  };

  const enqueueDestinationSave = useDestinationSaveQueue();
  const persistLineQuantity = (lineKey, rawValue, overrideLine) => enqueueDestinationSave(lineKey, () => saveLineQuantity(lineKey, rawValue, overrideLine));
  const saveLineQuantity = async (lineKey, rawValue, overrideLine) => {
    const targetLine = overrideLine || lines.find((line) => line.__key === lineKey);
    if (!targetLine || !receiving?.id || !currentReceivingPermissions(receiving).update || documentLocked) return;

    const normalizedValue = String(rawValue ?? "").trim();
    const numericValue = normalizedValue === "" ? 0 : Number.parseFloat(normalizedValue);

    if (!Number.isFinite(numericValue) || numericValue < 0) {
      setError(t("Received quantity must be a valid number equal to or above zero."));
      throw new Error("Received quantity must be a valid number equal to or above zero.");
    }

    const payload = {
      quantity_received: numericValue,
      ...(Object.prototype.hasOwnProperty.call(targetLine, "expiration_date") ? { expiration_date: expirationDatePayload(targetLine, items.find((item) => Number(item.id) === Number(getLineItemId(targetLine))) || targetLine.item) } : {}),
      overwrite: true,
      destination_location_id: receivingLineLocation(targetLine, receiving.destination_location_id, items, inventoryLocations),
    };

    try {
      if (!inventoryLocations.some((location) => Number(location.id) === Number(payload.destination_location_id))) throw new Error("Select an authorized receiving location before saving quantities.");
      if (targetLine.is_manual) {
        await axios.patch(
          `/api/purchasing/receivings/${receiving.id}/manual-line/${targetLine.id || targetLine.receipt_id}`,
          {
            item_id: getLineItemId(targetLine),
            ...payload,
          }
        );
      } else {
        await axios.patch(
          `/api/purchasing/receivings/${receiving.id}/line/${targetLine.po_line_id || targetLine.id}`,
          payload
        );
      }

      setLines((previousLines) =>
        previousLines.map((line) => {
          if (line.__key !== lineKey) return line;
          const unchanged = (line.expiration_date || null) === (targetLine.expiration_date || null) && Number(line.quantity_input || 0) === numericValue && Number(receivingLineLocation(line, receiving.destination_location_id, items, inventoryLocations)) === Number(payload.destination_location_id);
          return { ...line, quantity_received: numericValue,
            quantity_input: unchanged ? (numericValue === 0 ? "" : String(numericValue)) : line.quantity_input,
            __dirty: !unchanged, __saveError: unchanged ? false : line.__saveError,
          };
        })
      );
      setError("");
    } catch (persistError) {
      console.error("❌ [EditReceivingPopup] Error saving line quantity:", persistError);
      setLines((previous) => previous.map((line) => line.__key === lineKey ? { ...line, __dirty: true, __saveError: true } : line));
      setError(t(receivingErrorMessage(persistError, "Could not save the quantity for this receiving line.")));
      throw persistError;
    }
  };

  const changeLineDestination = (index, destination, source) => {
    const line = lines[index];
    if (!line || isLocked) return;
    const nextLine = { ...line, destination_location_id: destination, destination_location_source: source, __dirty: true };
    setLines((previous) => previous.map((entry, i) => i === index ? nextLine : entry));
    if (destination && Number(line.quantity_input || line.quantity_received) > 0) persistLineQuantity(line.__key, line.quantity_input || line.quantity_received, nextLine).catch(() => {});
  };
  useAuthorizedReceivingDestinations({
    locations: inventoryLocations, lines, items, headerLocationId: receiving?.destination_location_id,
    scopeKey: receiving?.id, disabled: !isOpen || !receiving?.id || loading || loadingLocations || isLocked || saving || Boolean(actionBusy),
    onChange: changeLineDestination,
  });
  const renderLineDestination = (line, index) => (
    <ReceivingLocationPicker compact readOnly={isLocked}
      locations={inventoryLocations} value={receivingLineLocation(line, receiving?.destination_location_id, items, inventoryLocations, { readOnly: isLocked })}
      label={`${t("Destination")}: ${line.item?.name || items.find((item) => Number(item.id) === Number(getLineItemId(line)))?.name || t("Item #{id}", { id: getLineItemId(line) })}`}
      disabled={loadingLocations || saving || Boolean(actionBusy)}
      onChange={(destination) => changeLineDestination(index, destination, "manual")} />
  );

  const openManualItemPicker = () => {
    setManualItemPicker({ open: true, query: "" });
  };

  const closeManualItemPicker = () => {
    setManualItemPicker({ open: false, query: "" });
  };

  const selectManualItem = (itemId) => {
    setManualItemId(String(itemId));
    setManualItemPicker({ open: false, query: "" });
    setError("");
  };

  const handleAddManualLine = async () => {
    if (!receiving?.id || documentLocked || !currentReceivingPermissions(receiving).update) return;

    const numericQty = Number.parseFloat(manualQty);
    if (!manualItemId || !Number.isFinite(numericQty) || numericQty <= 0) {
      setError(t("Select an item and enter a valid quantity before adding the manual line."));
      return;
    }

    const destinationLocationId = receivingLineLocation({ item_id: manualItemId }, receiving.destination_location_id, items, inventoryLocations);
    if (!inventoryLocations.some((location) => Number(location.id) === Number(destinationLocationId))) {
      setLocationSelectionRequest((request) => request + 1);
      setError(t("Select an authorized receiving location before saving quantities.")); return;
    }
    try {
      const response = await axios.post(
        `/api/purchasing/receivings/${receiving.id}/manual-line`,
        {
          item_id: manualItemId,
          quantity_received: numericQty,
          destination_location_id: destinationLocationId,
        }
      );

      const resolvedPrice = response.data?.unit_price_received ?? null;

      const newLine = normalizeLine({
        id: response.data?.id || Date.now(),
        manual_item_id: Number(manualItemId),
        item_id: Number(manualItemId),
        quantity_received: numericQty,
        destination_location_id: response.data?.destination_location_id || destinationLocationId,
        is_manual: true,
        item: selectedManualItem || { id: manualItemId, name: "Manual item" },
        order: null,
        unit_price_received: resolvedPrice,
        unit_price: resolvedPrice,
        pack_size: selectedManualItem?.pack_size || 1,
        unit:
          typeof selectedManualItem?.unit === "object"
            ? selectedManualItem.unit
            : { name: resolveUnitName(selectedManualItem?.unit) || "-" },
        subunit:
          typeof selectedManualItem?.subunit === "object"
            ? selectedManualItem.subunit
            : { name: resolveUnitName(selectedManualItem?.subunit) || "-" },
      });

      setLines((previousLines) => [newLine, ...previousLines]);
      setManualItemId("");
      setManualQty("");
      setShowManualPanel(false);
      setError("");
      tableWrapRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (manualError) {
      console.error("❌ [EditReceivingPopup] Error adding manual line:", manualError);
      setError(t(receivingErrorMessage(manualError, "Could not add this manual line to the receiving.")));
    }
  };

  const handleItemAdded = async (newItem) => {
    try {
      const refreshedItems = await axios.get("/api/purchasing/receivings/context");
      const enrichedItem = refreshedItems.data?.items?.find((item) => Number(item.id) === Number(newItem.id));
      if (!enrichedItem) throw new Error("The item is not available for receiving.");
      setItems((previousItems) => [enrichedItem, ...previousItems.filter((item) => item.id !== enrichedItem.id)]);
      setManualItemId(String(enrichedItem.id));
      setShowItemPopup(false);
      setShowManualPanel(true);
      setError("");
    } catch (itemError) {
      console.error("❌ [EditReceivingPopup] Error refreshing newly created item:", itemError);
      setShowItemPopup(false);
      setError(t("The new item was created, but its full data could not be reloaded."));
    }
  };

  const openPOLinePicker = async () => {
    if (!receiving?.supplier_id || isLocked) return;

    setPOLinePicker({ open: true, query: "", loading: true });
    setError("");

    try {
      const response = await axios.get(
        `/api/purchasing/receivings/pending-lines?supplier_id=${receiving.supplier_id}`
      );
      setPendingPOLines(response.data || []);
      setPOLinePicker({ open: true, query: "", loading: false });
    } catch (poError) {
      console.error("❌ [EditReceivingPopup] Error loading pending PO lines:", poError);
      setPOLinePicker({ open: false, query: "", loading: false });
      setError(t("Could not load pending purchase order lines for this supplier."));
    }
  };

  const closePOLinePicker = () => {
    setPOLinePicker({ open: false, query: "", loading: false });
  };

  const handleAddPOLine = async (poLine) => {
    if (!receiving?.id || documentLocked || !currentReceivingPermissions(receiving).update) return;

    try {
      await axios.post(`/api/purchasing/receivings/line`, {
        receiving_id: receiving.id,
        po_line_id: poLine.id,
        quantity_received: 0,
        destination_location_id: poLine.effective_destination_location_id || poLine.destination_location_id || receiving.destination_location_id || null,
      });

      await loadLines(receiving.id);
      closePOLinePicker();
      setError("");
    } catch (poError) {
      console.error("❌ [EditReceivingPopup] Error adding PO line:", poError);
      setError(t(receivingErrorMessage(poError, "Could not add that purchase order line to this receiving.")));
    }
  };

  const openConfirm = (type, line = null) => {
    setConfirmState({ open: true, type, line });
  };

  const closeConfirm = () => {
    if (saving || actionBusy) return;
    setConfirmState({ open: false, type: "", line: null });
  };

  const handleDeleteManualLine = async (line) => {
    if (documentLocked || !currentReceivingPermissions(receiving).delete) return;
    try {
      await enqueueDestinationSave(line.__key, () => axios.delete(
        `/api/purchasing/receivings/${receiving.id}/manual-line/${line.id || line.receipt_id}`
      ));
      setLines((previousLines) => previousLines.filter((entry) => entry.__key !== line.__key));
      setConfirmState({ open: false, type: "", line: null });
      setError("");
    } catch (deleteError) {
      console.error("❌ [EditReceivingPopup] Error deleting manual line:", deleteError);
      setError(t(receivingErrorMessage(deleteError, "Could not delete this manual line.")));
    }
  };

  const handleDeleteReceiving = async () => {
    if (!receiving?.id || documentLocked || !currentReceivingPermissions(receiving).delete) return;

    try {
      setActionBusy("delete");
      await axios.delete(`/api/purchasing/receivings/${receiving.id}`);
      dispatchRefresh();
      setConfirmState({ open: false, type: "", line: null });
      setActionBusy("");
      closePopup();
    } catch (deleteError) {
      console.error("❌ [EditReceivingPopup] Error deleting receiving:", deleteError);
      setActionBusy("");
      setError(t(receivingErrorMessage(deleteError, "Could not delete this receiving.")));
    }
  };

  const handleSaveAll = async () => {
    if (!receiving?.id || documentLocked || !currentReceivingPermissions(receiving).update) return;
    if (lines.some((line) => !receivingLineLocation(line, receiving.destination_location_id, items, inventoryLocations))) {
      setLocationSelectionRequest((request) => request + 1);
      return;
    }

    try {
      setSaving(true);
      setError("");

      await enqueueDestinationSave.flush();
      await saveHeader();

      const dirtyLines = lines.filter((line) => line.__dirty || (!line.destination_location_id && Number(line.quantity_input || 0) > 0));
      for (const line of dirtyLines) {
        await persistLineQuantity(line.__key, line.quantity_input);
      }

      if (currentReceivingPermissions(receiving).verify) await axios.post(`/api/purchasing/receivings/${receiving.id}/recalculate`);
      dispatchRefresh();
      setSaving(false);
      closePopup();
    } catch (saveError) {
      console.error("❌ [EditReceivingPopup] Error saving receiving:", saveError);
      setSaving(false);
      setError(t(receivingErrorMessage(saveError, "Could not save this receiving.")));
    }
  };

  const handleFinalizeReceiving = async () => {
    if (!receiving?.id || receiving.status !== "draft" || !currentReceivingPermissions(receiving).verify) return;
    try {
      setActionBusy("finalize");
      setError("");
      await enqueueDestinationSave.flush();
      if (currentReceivingPermissions(receiving).update) {
        await saveHeader();
        for (const line of lines.filter((entry) => entry.__dirty)) {
          await persistLineQuantity(line.__key, line.quantity_input);
        }
      }
      await axios.post("/api/purchasing/receivings", { receiving_id: receiving.id, supplier_id: receiving.supplier_id });
      setReceiving((previous) => ({ ...previous, status: "received" }));
      dispatchRefresh();
    } catch (finalizeError) {
      setError(t(receivingErrorMessage(finalizeError, "Could not save this receiving.")));
    } finally {
      setActionBusy("");
    }
  };

  const handleCreateDeliveryNote = async () => {
    if (!receiving?.id || documentLocked || !currentReceivingPermissions(receiving).confirmDeliveryNote) return;
    if (lines.some((line) => !receivingLineLocation(line, receiving.destination_location_id, items, inventoryLocations))) {
      setConfirmState({ open: false, type: "", line: null });
      setLocationSelectionRequest((request) => request + 1);
      return;
    }

    if (!String(receiving.delivery_note_number || "").trim()) {
      setError(t("Delivery note number is required before confirming the receiving."));
      return;
    }

    try {
      setActionBusy("confirm");
      setError("");

      await enqueueDestinationSave.flush();
      await saveHeader();

      const dirtyLines = lines.filter((line) => line.__dirty || (!line.destination_location_id && Number(line.quantity_input || 0) > 0));
      for (const line of dirtyLines) {
        await persistLineQuantity(line.__key, line.quantity_input);
      }

      const response = await axios.post(
        `/api/purchasing/receivings/${receiving.id}/create-delivery-note`
      );

      const deliveryNoteId = response.data?.delivery_note_id || response.data?.delivery_note?.id;
      if (!deliveryNoteId) {
        throw new Error("Delivery note created, but no ID was returned by the server.");
      }

      dispatchRefresh();
      setConfirmState({ open: false, type: "", line: null });
      setActionBusy("");
      closePopup({
        action: "confirm_delivery_note",
        receivingId: receiving.id,
        receivingStatus: response.data?.receiving_status || "verified",
        deliveryNoteId,
        deliveryNoteStatus: response.data?.delivery_note_status || null,
        alreadyExists: Boolean(response.data?.already_exists),
      });
      if (currentReceivingPermissions(receiving).viewDeliveryNotes) navigate(deliveryNotesPath);
    } catch (confirmError) {
      console.error("❌ [EditReceivingPopup] Error confirming receiving:", confirmError);
      setActionBusy("");
      setError(t(receivingErrorMessage(confirmError, "Could not create the delivery note.")));
    }
  };

  const handleConfirmAction = async () => {
    if (confirmState.type === "delete-manual" && confirmState.line) {
      await handleDeleteManualLine(confirmState.line);
      return;
    }

    if (confirmState.type === "delete-receiving") {
      await handleDeleteReceiving();
      return;
    }

    if (confirmState.type === "confirm-receiving") {
      await handleCreateDeliveryNote();
    }
  };

  const confirmCopy = useMemo(() => {
    if (confirmState.type === "delete-manual") {
      return {
        title: "Remove Manual Line",
        subtitle: "This line is not linked to any purchase order.",
        message: t("Remove {name} from the receiving?", { name: confirmState.line?.item?.name || t("this manual line") }),
        keepLabel: "Keep Line",
        actionLabel: "Remove Line",
      };
    }

    if (confirmState.type === "confirm-receiving") {
      return {
        title: "Confirm Receiving",
        subtitle: "This will create the delivery note from the current receiving.",
        message: t("Confirm {name} and create its delivery note now?", { name: receiving?.receiving_code || t("this receiving") }),
        keepLabel: "Keep Editing",
        actionLabel: actionBusy === "confirm" ? "Confirming..." : "Confirm Receiving",
      };
    }

    return {
      title: "Delete Receiving",
      subtitle: "This action cannot be undone.",
      message: t("Delete {name} permanently?", { name: receiving?.receiving_code || t("this receiving") }),
      keepLabel: "Keep Receiving",
      actionLabel: actionBusy === "delete" ? "Deleting..." : "Delete Receiving",
    };
  }, [actionBusy, confirmState.line, confirmState.type, receiving?.receiving_code, t]);

  const renderLineExpiration = (line) => (
    <ExpirationDateField line={line} item={items.find((item) => Number(item.id) === Number(getLineItemId(line))) || line.item}
      disabled={isLocked || saving || Boolean(actionBusy)}
      onChange={(value) => { if (!isLocked) setLines((previous) => previous.map((entry) =>
        entry.__key === line.__key ? { ...entry, expiration_date: value, __dirty: true } : entry)); }}
      onBlur={() => { if (line.__dirty && Number(line.quantity_input) > 0) persistLineQuantity(line.__key, line.quantity_input).catch(() => {}); }} />
  );

  const renderDesktopLines = () => (
    <div className="erp-tableShell">
      <div className="erp-tableWrap" ref={tableWrapRef}>
        <table className="erp-table">
          <thead>
            <tr>
              <th style={{ width: "16%" }}>{t("Order / Status")}</th>
              <th style={{ width: "25%" }}>{t("Item")}</th>
              <th style={{ width: "9%", textAlign: "right" }}>{t("Price")}</th>
              <th style={{ width: "7%", textAlign: "center" }} title={t("Received")}>Q</th>
              <th style={{ width: "12%" }}>{t("Location")}</th>
              <th style={{ width: "13%" }}>{t("Expiration date")}</th>
              <th style={{ width: "10%", textAlign: "center" }}>{t("Packaging")}</th>
              <th style={{ width: "8%", textAlign: "right" }}>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const itemData =
                items.find((item) => Number(item.id) === Number(getLineItemId(line))) || line.item;

              return (
                <tr key={line.__key}>
                  <td className="erp-tableCell--muted">
                    <div className="erp-itemName">{t(getOrderLabel(line))}</div>
                    <div className="erp-itemMeta">
                      {line.is_manual
                        ? t("Manual line")
                        : line.order?.expected_delivery_date
                          ? `ETA ${formatDateLabel(line.order.expected_delivery_date)}`
                          : t("Linked to purchase order")}
                    </div>
                  </td>

                  <td>
                    <div className="erp-tableItem">
                      <div className="erp-tableItemTop">
                        <span className="erp-itemName">
                          {itemData?.name || t("Item #{id}", { id: getLineItemId(line) })}
                        </span>
                        {line.is_manual && <span className="erp-miniBadge">{t("Manual")}</span>}
                      </div>
                      <div className="erp-itemMeta">
                        {itemData?.code ? `${itemData.code} · ` : ""}
                        {resolvePackaging(itemData || line)}
                      </div>
                    </div>
                  </td>

                  <td style={{ textAlign: "right" }} className="erp-qtyCell">
                    {formatMoney(getLinePrice(line))}
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={quantityInputValue(line)}
                      aria-invalid={line.__saveError || undefined}
                      title={line.__saveError ? t("This quantity has not been saved.") : undefined}
                      disabled={isLocked || saving || Boolean(actionBusy) || !inventoryLocations.some((location) => Number(location.id) === Number(receivingLineLocation(line, receiving.destination_location_id, items, inventoryLocations)))}
                      onChange={(event) => handleLineInputChange(line.__key, event.target.value)}
                      onBlur={() => persistLineQuantity(line.__key, line.quantity_input).catch(() => {})}
                      className="form-control erp-tableInput"
                      placeholder="0.00"
                    />
                    {line.__saveError && <small className="receiving-quantity-error">{t("This quantity has not been saved.")}</small>}
                  </td>

                  <td>{renderLineDestination(line, index)}</td>
                  <td>{renderLineExpiration(line)}</td>
                  <td className="erp-qtyCell">{resolvePackaging(itemData || line)}</td>

                  <td style={{ textAlign: "right" }}>
                    {line.is_manual ? (
                      <button
                        type="button"
                        className="erp-btn erp-btn--danger"
                        disabled={documentLocked || !access.delete || saving || !!actionBusy}
                        onClick={() => openConfirm("delete-manual", line)}
                      > {t("Remove")} </button>
                    ) : (
                      <span className="erp-tableCell--muted">{t("PO line")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderMobileLines = () => (
    <div className="erp-mobileLines">
      {lines.map((line, index) => {
        const itemData =
          items.find((item) => Number(item.id) === Number(getLineItemId(line))) || line.item;

        return (
          <div className="erp-lineCard" key={line.__key}>
            <div className="erp-lineCardTop">
              <div>
                <div className="erp-lineCardTitle">
                  {itemData?.name || t("Item #{id}", { id: getLineItemId(line) })}
                </div>
                <div className="erp-lineCardSub">
                  {line.is_manual
                    ? t("Manual line")
                    : `${t(getOrderLabel(line))}${line.order?.expected_delivery_date ? ` · ETA ${formatDateLabel(line.order.expected_delivery_date)}` : ""}`}
                </div>
              </div>
              {line.is_manual && <span className="erp-miniBadge">{t("Manual")}</span>}
            </div>

            <div className="erp-lineCardMeta">
              {itemData?.code ? `${itemData.code} · ` : ""}
              {resolvePackaging(itemData || line)}
            </div>

            <div className="erp-lineGrid">
              <div className="erp-lineMeta">
                <span className="erp-lineMetaLabel">{t("Price")}</span>
                <span className="erp-lineMetaValue">{formatMoney(getLinePrice(line))}</span>
              </div>
              <div className="erp-lineMeta">
                <span className="erp-lineMetaLabel">{t("Packaging")}</span>
                <span className="erp-lineMetaValue">{resolvePackaging(itemData || line)}</span>
              </div>
              <div className="erp-lineMeta">
                <span className="erp-lineMetaLabel" title={t("Received")}>Q</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quantityInputValue(line)}
                      aria-invalid={line.__saveError || undefined}
                      title={line.__saveError ? t("This quantity has not been saved.") : undefined}
                      disabled={isLocked || saving || Boolean(actionBusy) || !inventoryLocations.some((location) => Number(location.id) === Number(receivingLineLocation(line, receiving.destination_location_id, items, inventoryLocations)))}
                  onChange={(event) => handleLineInputChange(line.__key, event.target.value)}
                  onBlur={() => persistLineQuantity(line.__key, line.quantity_input).catch(() => {})}
                  className="form-control erp-tableInput"
                  placeholder="0.00"
                />
                    {line.__saveError && <small className="receiving-quantity-error">{t("This quantity has not been saved.")}</small>}
              </div>
              <div className="erp-lineMeta">
                <span className="erp-lineMetaLabel">{t("Location")}</span>
                {renderLineDestination(line, index)}
              </div>
              <div className="erp-lineMeta">
                <span className="erp-lineMetaLabel">{t("Expiration date")}</span>
                {renderLineExpiration(line)}
              </div>
            </div>

            <div className="erp-lineActions">
              {line.is_manual ? (
                <button
                  type="button"
                  className="erp-btn erp-btn--danger"
                  disabled={documentLocked || !access.delete || saving || !!actionBusy}
                  onClick={() => openConfirm("delete-manual", line)}
                > {t("Remove Line")} </button>
              ) : (
                <span className="erp-tableCell--muted">{t("PO line")}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderMainSection = () => (
    <>
      <div className="erp-section">
        <h6 className="erp-sectionTitle">{t("Receiving Header")}</h6>
        <p className="erp-sectionSub"> {t("Keep the header compact and let the lines do the real work. This edit view focuses on quick corrections.")} </p>

        <div className="erp-row erp-row--header">
          <div className="erp-field">
            <label className="form-label">{t("Receiving Code")}</label>
            <input
              type="text"
              readOnly
              value={receiving?.receiving_code || ""}
              className="form-control erp-readonly"
            />
          </div>

          <div className="erp-field">
            <label className="form-label">{t("Supplier")}</label>
            <input
              type="text"
              readOnly
              value={receiving?.supplier?.name || "-"}
              className="form-control erp-readonly"
            />
          </div>

          <div className="erp-field">
            <label className="form-label">{t("Delivery Note Number")}</label>
            <input
              type="text"
              value={receiving?.delivery_note_number || ""}
              onChange={(event) => updateReceivingField("delivery_note_number", event.target.value)}
              className="form-control"
              placeholder={t("Supplier delivery note number...")}
              readOnly={isLocked}
            />
          </div>

          <div className="erp-field">
            <label className="form-label">{t("Received Date")}</label>
            <input
              type="date"
              value={receiving?.received_date || today()}
              onChange={(event) => updateReceivingField("received_date", event.target.value || today())}
              className="form-control"
              readOnly={isLocked}
            />
          </div>

          <div className="erp-field">
            <label className="form-label">{t("Destination Location")}</label>
            <ReceivingLocationPicker allowInherited readOnly={isLocked} value={receiving?.destination_location_id || ""} locations={inventoryLocations}
              requestOpen={locationSelectionRequest}
              label={t("Destination Location")} onChange={(value) => updateReceivingField("destination_location_id", value)} disabled={isLocked || loadingLocations || saving || Boolean(actionBusy)} />
          </div>

          <div className="erp-field">
            <label className="form-label">{t("Initiated By")}</label>
            <input
              type="text"
              readOnly
              value={formatDocumentInitiator(receiving)}
              className="form-control erp-readonly"
            />
          </div>
        </div>
      </div>

      <div className="erp-section">
        {!loadingLocations && !inventoryLocations.length && <p role="alert" className="receiving-quantity-error">{t("You have no authorized locations for receiving goods.")}</p>}
        <div className="erp-linesTop">
          <div>
            <h6>{t("Receiving Lines")}</h6>
            <p> {t("Edit received quantities directly, add manual items when the supplier brought extras, or attach pending PO lines without leaving the popup.")} </p>
          </div>

          {!isLocked && (
            <div className="erp-inlineActions">
              <button
                type="button"
                className={`erp-btn ${showManualPanel ? "erp-btn--soft" : "erp-btn--cyan"}`}
                onClick={() => setShowManualPanel((previousValue) => !previousValue)}
              >
                {showManualPanel ? t("Hide Manual Add") : t("+ Add Item Not in PO")}
              </button>
              <button
                type="button"
                className="erp-btn erp-btn--soft"
                onClick={openPOLinePicker}
              > {t("+ Add from Purchase Order")} </button>
            </div>
          )}
        </div>

        {showManualPanel && !isLocked && (
          <div className="erp-manualCard">
            <div className="erp-manualHead">
              <div>
                <h6>{t("Manual Receiving Line")}</h6>
                <p>{t("Use this when the supplier delivered something that is not already attached to the receiving.")}</p>
              </div>
            </div>

            <div className="erp-manualGrid">
              <div className="erp-field">
                <label className="form-label">{t("Item *")}</label>
                {isMobile ? (
                  <select
                    value={manualItemId}
                    onChange={(event) => selectManualItem(event.target.value)}
                    className="form-select"
                  >
                    <option value="">{t("Select item")}</option>
                    {visibleItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.code ? `${item.code} - ${item.name}` : item.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button type="button" className="erp-btn erp-pickerTrigger" onClick={openManualItemPicker}>
                    <span>{selectedManualItem ? (selectedManualItem.code ? `${selectedManualItem.code} - ${selectedManualItem.name}` : selectedManualItem.name) : t("Select item")}</span>
                    <span>{manualItemPicker.open ? t("Close") : t("Choose")}</span>
                  </button>
                )}
              </div>

              <div className="erp-field">
                <label className="form-label">{t("Qty *")}</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={manualQty}
                  onChange={(event) => setManualQty(event.target.value)}
                  className="form-control"
                  placeholder="0.00"
                />
              </div>

              <div className="erp-infoCard">
                <span className="erp-infoLabel">{t("Packaging")}</span>
                <span className="erp-infoValue">
                  {selectedManualItem ? resolvePackaging(selectedManualItem) : "—"}
                </span>
              </div>
            </div>

            <div className="erp-manualActions">
              <button type="button" className="erp-btn" disabled={!access.createItem} onClick={() => { if (currentReceivingPermissions(receiving).createItem) setShowItemPopup(true); }}> {t("+ New Item")} </button>
              <button
                type="button"
                className="erp-btn"
                onClick={() => {
                  setShowManualPanel(false);
                  setManualItemId("");
                  setManualQty("");
                  setError("");
                }}
              > {t("Cancel")} </button>
              <button type="button" className="erp-btn erp-btn--primary" onClick={handleAddManualLine}> {t("Add Line")} </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="erp-emptyState">
            <strong>{t("Loading receiving data...")}</strong>
            <span>{t("Fetching header and lines for this receiving.")}</span>
          </div>
        ) : lines.length ? (
          isMobile ? renderMobileLines() : renderDesktopLines()
        ) : (
          <div className="erp-emptyState">
            <strong>{t("No lines in this receiving yet.")}</strong>
            <span> {t("You can attach pending purchase order lines or add a manual line if something arrived outside the normal PO flow.")} </span>
          </div>
        )}

        {error && <div className="erp-alert">{t(error)}</div>}

        {!documentLocked && (
          <div className="erp-actions">
            {access.delete && <button
              type="button"
              className="erp-btn erp-btn--danger"
              onClick={() => openConfirm("delete-receiving")}
              disabled={saving || !!actionBusy}
            > {t("Delete Receiving")} </button>}
            {access.update && <button
              type="button"
              className="erp-btn"
              onClick={handleSaveAll}
              disabled={saving || !!actionBusy}
            >
              {saving ? t("Saving...") : t("Save Changes")}
            </button>}
            {receiving?.status === "draft" && access.verify && <button
              type="button"
              className="erp-btn erp-btn--primary"
              onClick={handleFinalizeReceiving}
              disabled={saving || !!actionBusy}
            >{t("Confirm Receiving")}</button>}
            {receiving?.status === "received" && access.confirmDeliveryNote && <button
              type="button"
              className="erp-btn erp-btn--primary"
              onClick={() => openConfirm("confirm-receiving")}
              disabled={saving || !!actionBusy}
            > {t("Confirm / Create Delivery Note")} </button>}
          </div>
        )}
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <>
      <style>{numberInputStyle}</style>
      <style>{popupCss}</style>

      <div
        className="erp-wrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closePopup();
        }}
      >
        <div className="erp-modal" role="dialog" aria-modal="true" aria-label={t("Edit Receiving")} onMouseDown={(event) => event.stopPropagation()}>
          <div className="erp-head">
            <div className="erp-headMain">
              <h5 className="erp-title">
                {isLocked ? t("View Receiving") : t("Edit Receiving")}
              </h5>
              <div className="erp-sub">
                {receiving
                  ? `${receiving.receiving_code || t("Receiving #{id}", { id: receiving.id })} · ${receiving.supplier?.name || t("Supplier")}`
                  : t("Loading receiving...")}
              </div>
            </div>

            <div className="erp-headActions">
              <span
                className={`erp-badge ${
                  receiving?.status === "verified" || receiving?.status === "received"
                    ? "erp-badge--done"
                    : ""
                }`}
              >
                {receivingPopupStatus(t, receiving?.status)}
              </span>
              <button
                type="button"
                className="erp-closeBtn"
                aria-label={t("Close receiving edit popup")}
                title={t("Close")}
                onClick={closePopup}
              >
                {isMobile ? t("Close") : t("X")}
              </button>
            </div>
          </div>

          <div className="erp-body">{renderMainSection()}</div>
        </div>

        {confirmState.open && (
          <div
            className="erp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeConfirm();
            }}
          >
            <div className="erp-pickerCard" onMouseDown={(event) => event.stopPropagation()}>
              <div className="erp-pickerHead">
                <div>
                  <h6>{t(confirmCopy.title)}</h6>
                  <p>{t(confirmCopy.subtitle)}</p>
                </div>
                <button
                  type="button"
                  className="erp-btn"
                  onClick={closeConfirm}
                  disabled={saving || !!actionBusy}
                > {t("Close")} </button>
              </div>

              <div className="erp-pickerBody">
                <p className="erp-confirmText">
                  <strong>{confirmCopy.message}</strong>
                </p>

                <div className="erp-pickerActions">
                  <button
                    type="button"
                    className="erp-btn"
                    onClick={closeConfirm}
                    disabled={saving || !!actionBusy}
                  >
                    {t(confirmCopy.keepLabel)}
                  </button>
                  <button
                    type="button"
                    className={`erp-btn erp-btn--${confirmState.type === "confirm-receiving" ? "success" : "danger"}`}
                    onClick={handleConfirmAction}
                    disabled={saving || !!actionBusy}
                  >
                    {t(confirmCopy.actionLabel)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isMobile && manualItemPicker.open && (
          <div
            className="erp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeManualItemPicker();
            }}
          >
            <div
              className="erp-pickerCard erp-pickerCard--wide"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="erp-pickerHead">
                <div>
                  <h6>{t("Select Item")}</h6>
                  <p>{t("Pick the item with a clean Pilcore modal instead of the old inline dropdown.")}</p>
                </div>
                <button type="button" className="erp-btn" onClick={closeManualItemPicker}> {t("Close")} </button>
              </div>

              <div className="erp-pickerBody">
                <input
                  type="search"
                  value={manualItemPicker.query}
                  onChange={(event) =>
                    setManualItemPicker((previousPicker) => ({
                      ...previousPicker,
                      query: event.target.value,
                    }))
                  }
                  className="form-control erp-pickerSearch"
                  placeholder={t("Search by code or item name...")}
                  autoFocus
                />

                <div className="erp-pickerList">
                  {filteredManualItems.length ? (
                    filteredManualItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="erp-pickerItem"
                        onClick={() => selectManualItem(item.id)}
                      >
                        <span className="erp-pickerItemTitle">
                          {item.code ? `${item.code} - ${item.name}` : item.name}
                        </span>
                        <span className="erp-pickerItemMeta"> {t("Packaging:")} {resolvePackaging(item)}
                          {item.reference ? ` · Ref: ${item.reference}` : ""}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="erp-pickerEmpty">{t("No items match your search.")}</div>
                  )}
                </div>

                <div className="erp-pickerActions">
                  <button
                    type="button"
                    className="erp-btn erp-btn--cyan"
                    disabled={!access.createItem} onClick={() => { if (currentReceivingPermissions(receiving).createItem) setShowItemPopup(true); }}
                  > {t("+ New Item")} </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {poLinePicker.open && (
          <div
            className="erp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closePOLinePicker();
            }}
          >
            <div
              className="erp-pickerCard erp-pickerCard--wide"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="erp-pickerHead">
                <div>
                  <h6>{t("Add Pending PO Line")}</h6>
                  <p>{t("Attach another pending purchase order line without leaving the receiving edit flow.")}</p>
                </div>
                <button type="button" className="erp-btn" onClick={closePOLinePicker}> {t("Close")} </button>
              </div>

              <div className="erp-pickerBody">
                <input
                  type="search"
                  value={poLinePicker.query}
                  onChange={(event) =>
                    setPOLinePicker((previousPicker) => ({
                      ...previousPicker,
                      query: event.target.value,
                    }))
                  }
                  className="form-control erp-pickerSearch"
                  placeholder={t("Search by PO code or item...")}
                  autoFocus
                />

                {poLinePicker.loading ? (
                  <div className="erp-pickerEmpty">{t("Loading pending purchase order lines...")}</div>
                ) : filteredPendingPOLines.length ? (
                  <div className="erp-pickerList">
                    {filteredPendingPOLines.map((line) => (
                      <div className="erp-candidateCard" key={line.id}>
                        <div className="erp-candidateTop">
                          <div>
                            <div className="erp-candidateTitle">
                              {line.item?.name || t("Item #{id}", { id: line.item_id })}
                            </div>
                            <div className="erp-pickerItemMeta">
                              {line.order?.order_code || t("PO")} {t("· ETA")} {formatDateLabel(line.order?.expected_delivery_date)}
                            </div>
                          </div>

                          <div className="erp-candidateActions">
                            <button
                              type="button"
                              className="erp-btn erp-btn--cyan"
                              onClick={() => handleAddPOLine(line)}
                            > {t("Add")} </button>
                          </div>
                        </div>

                        <div className="erp-candidateMeta">
                          <div className="erp-candidateMetaBox">
                            <span className="erp-candidateMetaLabel">{t("Ordered")}</span>
                            <span className="erp-lineMetaValue">{formatQty(line.quantity)}</span>
                          </div>
                          <div className="erp-candidateMetaBox">
                            <span className="erp-candidateMetaLabel">{t("Received")}</span>
                            <span className="erp-lineMetaValue">{formatQty(line.quantity_received)}</span>
                          </div>
                          <div className="erp-candidateMetaBox">
                            <span className="erp-candidateMetaLabel">{t("Pending")}</span>
                            <span className="erp-lineMetaValue">
                              {formatQty(Number(line.quantity || 0) - Number(line.quantity_received || 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="erp-pickerEmpty"> {t("No pending purchase order lines are available for this supplier.")} </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showItemPopup && access.createItem && !isLocked && (
          <AddItemPopup
            isOpen={showItemPopup}
            context="receiving"
            supplierIdLock={receiving?.supplier_id || null}
            onClose={() => setShowItemPopup(false)}
            onAdded={handleItemAdded}
          />
        )}
      </div>
    </>
  );
}
